import { useEffect, useState, type KeyboardEvent } from "react";
import type { Authorization, QuarterTable, ReportSettings } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { saveFile } from "../../lib/files";
import { quarterCell, quarterTotals } from "./reports";

interface Props {
  authorization: Authorization;
  settings: ReportSettings;
  onSaved: (settings: ReportSettings) => void;
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Boekhouder, as in the prototype: per quarter an Excel with one line per day (Z), to download or to
 * look at here, and optionally mailed to the accountant after every quarter.
 */
export function AccountantTab({ authorization, settings, onSaved }: Props) {
  const { api, report, toast, reportsRevision } = useTill();
  const [quarters, setQuarters] = useState<{ key: string; label: string }[] | null>(null);
  const [chosen, setChosen] = useState("");
  const [table, setTable] = useState<QuarterTable | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const text = t.reports.accountant;
  // where the Z's of the shop go is the owner's choice (the server refuses it to anybody else)
  const mayChange = authorization.staff.can.includes("settings.edit");

  useEffect(() => {
    let current = true;
    api
      .request<{ quarters: { key: string; label: string }[] }>("GET", "/device/reports/quarters", {
        staffToken: authorization.token,
        retry: true,
      })
      .then((answer) => {
        if (current) setQuarters(answer.quarters);
      })
      .catch(report);
    return () => {
      current = false;
    };
  }, [api, authorization.token, report, reportsRevision]);

  const key = quarters?.some((quarter) => quarter.key === chosen) ? chosen : (quarters?.[0]?.key ?? "");
  const fetchTable = () =>
    api.request<QuarterTable>("GET", `/device/reports/quarters/${key}`, { staffToken: authorization.token });

  const show = () => {
    fetchTable().then(setTable).catch(report);
  };

  const download = async () => {
    setBusy(true);
    try {
      const fresh = await fetchTable();
      setTable(fresh);
      if (fresh.rows.length === 0) {
        toast(text.noData);
        return;
      }
      const file = await api.download(`/device/reports/quarters/${key}/xlsx`, { staffToken: authorization.token });
      saveFile(file);
      toast(text.downloaded(file.filename));
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };

  const save = async (accountant: ReportSettings["accountant"], message?: string) => {
    // shown at once (the checkbox follows the tap); the server's answer follows
    onSaved({ ...settings, accountant });
    try {
      const answer = await api.request<{ settings: ReportSettings }>("PUT", "/device/reports/settings", {
        staffToken: authorization.token,
        body: { platforms: settings.platforms, accountant },
      });
      onSaved(answer.settings);
      if (message) toast(message);
    } catch (error) {
      onSaved(settings);
      report(error);
    }
  };

  const saveEmail = () => {
    if (email === null) return;
    const typed = email.trim();
    if (typed === settings.accountant.email) {
      setEmail(null);
      return;
    }
    if (typed && !EMAIL.test(typed)) {
      toast(text.badEmail, "bad");
      return;
    }
    setEmail(null);
    void save({ on: settings.accountant.on, email: typed }, text.saved(typed));
  };

  return (
    <>
      <p className="note rsince">{text.note}</p>
      <div className="rowacts">
        <select
          className="qsel"
          aria-label={text.quarter}
          value={key}
          onChange={(event) => {
            setChosen(event.target.value);
            setTable(null);
          }}
        >
          {(quarters ?? []).map((quarter) => (
            <option key={quarter.key} value={quarter.key}>
              {quarter.label}
            </option>
          ))}
        </select>
        <button type="button" className="btn primary" disabled={!key || busy} onClick={() => void download()}>
          {text.download}
        </button>
        <button type="button" className="btn" disabled={!key} onClick={show}>
          {text.show}
        </button>
      </div>
      <label className="inl qmail">
        <input
          type="checkbox"
          checked={settings.accountant.on}
          disabled={!mayChange}
          onChange={(event) => {
            void save({ on: event.target.checked, email: (email ?? settings.accountant.email).trim() });
          }}
        />{" "}
        {text.mail}
      </label>
      {settings.accountant.on && (
        <input
          className="qmailto"
          type="email"
          inputMode="email"
          placeholder={text.email}
          aria-label={text.email}
          disabled={!mayChange}
          value={email ?? settings.accountant.email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          onBlur={saveEmail}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
        />
      )}
      {!mayChange && <p className="note">{text.ownerOnly}</p>}
      {table && (
        <div className="qout">
          {table.rows.length === 0 ? (
            <p className="note">{text.none}</p>
          ) : (
            <div className="tablewrap qtab">
              <table>
                <thead>
                  <tr>
                    {table.columns.map((column) => (
                      <th key={column.header} className={column.kind === "date" ? "" : "r"}>
                        {column.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, index) => (
                    <tr key={index}>
                      {table.columns.map((column, at) => (
                        <td key={column.header} className={column.kind === "date" ? "" : "r num"}>
                          {quarterCell(column.kind, row[at] ?? null)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="qtot">
                    {quarterTotals(table).map((value, at) => (
                      <td key={table.columns[at]!.header} className={table.columns[at]!.kind === "date" ? "" : "r num"}>
                        <b>{value}</b>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}
